/**
 * Group Reward Template Edit Page
 * Create or edit a reward template
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Grid,
  Autocomplete,
  Avatar,
} from '@mui/material'
import { Save, ArrowBack, Groups } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { searchApi } from '@api/endpoints/search'
import {
  GiftRewardContentType,
  DistributionType,
  ExpirationType,
  getContentTypeName,
  getDistributionTypeName,
  getExpirationTypeName,
  type DistributionConfig,
} from '@api/types/rewards'
import type { CreateGiftRewardTemplateRequest } from '@api/types/rewards'
import { useRewardTemplate, useCreateTemplate, useUpdateTemplate } from '../hooks/useRewardTemplates'
import DistributionConfigEditor from '../components/DistributionConfigEditor'

export default function GroupRewardTemplateEditPage() {
  const { id: groupId, templateId } = useParams<{ id: string; templateId?: string }>()
  const navigate = useNavigate()
  const isEditing = !!templateId

  // Fetch existing template if editing
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useRewardTemplate(groupId, templateId)

  // Mutations
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate()

  // Form state
  const [formData, setFormData] = useState<CreateGiftRewardTemplateRequest>({
    groupId: groupId || null,
    title: '',
    description: '',
    contentType: GiftRewardContentType.Text,
    content: null,
    distributionType: DistributionType.Manual,
    distributionConfig: null,
    totalLimit: null,
    dailyLimit: null,
    weeklyLimit: null,
    yearlyLimit: null,
    usesPerInstance: 1,
    isTransferable: false,
    allowPostExpirationRedemption: false,
    expirationType: ExpirationType.Never,
    expirationDays: null,
    fixedExpirationDate: null,
    authorizedScannerGroupId: null,
  })
  const [error, setError] = useState<string | null>(null)

  // Content-specific state
  const [textContent, setTextContent] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')

  // Authorized scanner group state
  const [scannerGroupSearch, setScannerGroupSearch] = useState('')
  const [selectedScannerGroup, setSelectedScannerGroup] = useState<{
    id: string
    name: string
    avatar?: string
  } | null>(null)

  // Search for groups
  const { data: groupSearchResults, isLoading: isSearchingGroups } = useQuery({
    queryKey: ['group-search', scannerGroupSearch],
    queryFn: async () => {
      const results = await searchApi.quickSearch(scannerGroupSearch, 'groups', 10)
      // Handle both array and object response formats
      const groups = Array.isArray(results) ? results : (results as any)?.groups || []
      return groups.map((g: any) => ({
        id: g.id,
        name: g.name || g.displayName,
        avatar: g.groupImage || g.avatar,
      }))
    },
    enabled: scannerGroupSearch.length >= 2,
  })

  // Populate form when editing
  useEffect(() => {
    if (existingTemplate) {
      setFormData({
        groupId: existingTemplate.groupId,
        title: existingTemplate.title || '',
        description: existingTemplate.description || '',
        contentType: existingTemplate.contentType,
        content: existingTemplate.content,
        distributionType: existingTemplate.distributionType,
        distributionConfig: existingTemplate.distributionConfig,
        totalLimit: existingTemplate.totalLimit,
        dailyLimit: existingTemplate.dailyLimit,
        weeklyLimit: existingTemplate.weeklyLimit,
        yearlyLimit: existingTemplate.yearlyLimit,
        usesPerInstance: existingTemplate.usesPerInstance,
        isTransferable: existingTemplate.isTransferable,
        allowPostExpirationRedemption: existingTemplate.allowPostExpirationRedemption,
        expirationType: existingTemplate.expirationType,
        expirationDays: existingTemplate.expirationDays,
        fixedExpirationDate: existingTemplate.fixedExpirationDate,
        authorizedScannerGroupId: existingTemplate.authorizedScannerGroupId,
      })
      // Extract content values
      const content = existingTemplate.content as Record<string, unknown> | null
      if (content) {
        setTextContent((content.textContent as string) || '')
        setLinkUrl((content.linkUrl as string) || '')
        setImageUrl((content.imageUrl as string) || '')
      }
      // Set authorized scanner group if exists
      if (existingTemplate.authorizedScannerGroupId) {
        // We'll set a placeholder - ideally the API would return the group name
        setSelectedScannerGroup({
          id: existingTemplate.authorizedScannerGroupId,
          name: (existingTemplate as any).authorizedScannerGroupName || 'Selected Group',
          avatar: (existingTemplate as any).authorizedScannerGroupAvatar,
        })
      }
    }
  }, [existingTemplate])

  const handleSubmit = async () => {
    setError(null)

    // Build content object based on type
    let content: Record<string, unknown> | null = null
    switch (formData.contentType) {
      case GiftRewardContentType.Text:
        content = { textContent }
        break
      case GiftRewardContentType.URL:
        content = { linkUrl }
        break
      case GiftRewardContentType.Image:
        content = { imageUrl }
        break
      case GiftRewardContentType.QR_Redeemable:
        content = {} // QR content is auto-generated
        break
      case GiftRewardContentType.Adventure_Unlock:
        // Adventure unlock would need adventure selector
        content = {}
        break
    }

    const submitData: CreateGiftRewardTemplateRequest = {
      ...formData,
      content,
    }

    try {
      if (!groupId) {
        setError('Group ID is required')
        return
      }
      if (isEditing && templateId) {
        await updateMutation.mutateAsync({
          groupId,
          templateId,
          data: submitData,
        })
      } else {
        await createMutation.mutateAsync({ groupId, data: submitData })
      }
      navigate(`/groups/${groupId}/rewards`)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save template')
    }
  }

  if (isEditing && isLoadingTemplate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/groups/${groupId}/rewards`)}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          {isEditing ? 'Edit Template' : 'Create Template'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Content Type
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={formData.contentType}
                label="Content Type"
                onChange={(e) =>
                  setFormData({ ...formData, contentType: e.target.value as GiftRewardContentType })
                }
              >
                {Object.values(GiftRewardContentType)
                  .filter((v) => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {getContentTypeName(type as GiftRewardContentType)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Content fields based on type */}
            {formData.contentType === GiftRewardContentType.Text && (
              <TextField
                fullWidth
                label="Text Content"
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                multiline
                rows={4}
                helperText="The text content that will be shown when the reward is redeemed"
              />
            )}

            {formData.contentType === GiftRewardContentType.URL && (
              <TextField
                fullWidth
                label="Link URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                type="url"
                helperText="The URL that will be available when the reward is redeemed"
              />
            )}

            {formData.contentType === GiftRewardContentType.Image && (
              <TextField
                fullWidth
                label="Image URL"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                type="url"
                helperText="The image URL to display"
              />
            )}

            {formData.contentType === GiftRewardContentType.QR_Redeemable && (
              <Alert severity="info">
                QR Redeemable rewards will generate a unique code that can be scanned to redeem.
              </Alert>
            )}
          </Paper>

          {/* Distribution Settings */}
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Distribution Settings
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Distribution Type</InputLabel>
              <Select
                value={formData.distributionType}
                label="Distribution Type"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    distributionType: e.target.value as DistributionType,
                    distributionConfig: null, // Reset config when type changes
                  })
                }
              >
                {Object.values(DistributionType)
                  .filter((v) => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {getDistributionTypeName(type as DistributionType)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <DistributionConfigEditor
              distributionType={formData.distributionType}
              config={formData.distributionConfig as DistributionConfig}
              onChange={(config) =>
                setFormData({ ...formData, distributionConfig: config as Record<string, unknown> })
              }
            />

            <Divider sx={{ my: 3 }} />

            <TextField
              fullWidth
              label="Total Limit"
              type="number"
              value={formData.totalLimit || ''}
              onChange={(e) =>
                setFormData({ ...formData, totalLimit: e.target.value ? parseInt(e.target.value) : null })
              }
              helperText="Maximum total rewards to distribute (leave empty for unlimited)"
              sx={{ mb: 2 }}
            />
          </Paper>
        </Grid>

        {/* Sidebar settings */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Instance Settings
            </Typography>

            <TextField
              fullWidth
              label="Uses Per Instance"
              type="number"
              value={formData.usesPerInstance || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  usesPerInstance: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              helperText="How many times each reward can be redeemed"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Expiration Type</InputLabel>
              <Select
                value={formData.expirationType}
                label="Expiration Type"
                onChange={(e) =>
                  setFormData({ ...formData, expirationType: e.target.value as ExpirationType })
                }
              >
                {Object.values(ExpirationType)
                  .filter((v) => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {getExpirationTypeName(type as ExpirationType)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {formData.expirationType === ExpirationType.DaysFromClaim && (
              <TextField
                fullWidth
                label="Days Until Expiration"
                type="number"
                value={formData.expirationDays || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expirationDays: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                sx={{ mb: 2 }}
              />
            )}

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isTransferable}
                  onChange={(e) => setFormData({ ...formData, isTransferable: e.target.checked })}
                />
              }
              label="Allow Transfer"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowPostExpirationRedemption}
                  onChange={(e) =>
                    setFormData({ ...formData, allowPostExpirationRedemption: e.target.checked })
                  }
                />
              }
              label="Allow Post-Expiration Redemption"
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Authorized Scanner Group
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Select a group that can scan and redeem these rewards (optional)
            </Typography>

            <Autocomplete
              value={selectedScannerGroup}
              onChange={(_, newValue) => {
                setSelectedScannerGroup(newValue)
                setFormData({
                  ...formData,
                  authorizedScannerGroupId: newValue?.id || null,
                })
              }}
              inputValue={scannerGroupSearch}
              onInputChange={(_, newInputValue) => {
                setScannerGroupSearch(newInputValue)
              }}
              options={groupSearchResults || []}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isSearchingGroups}
              filterOptions={(x) => x} // Disable built-in filtering since we're using server-side search
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Avatar
                    src={option.avatar}
                    variant="rounded"
                    sx={{ width: 32, height: 32, mr: 1.5, borderRadius: 1 }}
                  >
                    <Groups fontSize="small" />
                  </Avatar>
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for a group..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: selectedScannerGroup ? (
                      <Avatar
                        src={selectedScannerGroup.avatar}
                        variant="rounded"
                        sx={{ width: 24, height: 24, mr: 1, borderRadius: 0.5 }}
                      >
                        <Groups fontSize="small" />
                      </Avatar>
                    ) : null,
                    endAdornment: (
                      <>
                        {isSearchingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={scannerGroupSearch.length < 2 ? 'Type to search...' : 'No groups found'}
            />
          </Paper>

          {/* Save button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={isPending ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSubmit}
            disabled={isPending || !formData.title}
          >
            {isPending ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
